import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiStore } from "@finitless/shared/stores";
import { invokeLinkInBioAdmin } from "../lib/linkInBioAdminApi";

/** One row for edit: keyed by profile UUID only (server resolves `business_id`). */
const adminDetailQueryKey = (profileId, project) => [
  "link-in-bio-admin",
  "detail",
  profileId ?? "",
  project ?? "",
];

const listKey = (businessId, project) => [
  "link-in-bio-list",
  businessId,
  project ?? "",
];

/** One `list_all` query per Finitless API project (server resolves businesses from token). */
const franchiseListKey = (project) => [
  "link-in-bio-list",
  "franchise",
  project ?? "",
];

/**
 * List non-archived link-in-bio profiles for a business.
 * @param {string | number | null | undefined} businessId
 */
export function useLinkInBioList(businessId) {
  const id = businessId != null ? String(businessId) : "";
  const project = useApiStore((s) => s.project);

  return useQuery({
    queryKey: listKey(id, project),
    queryFn: async () => {
      const res = await invokeLinkInBioAdmin("list", { business_id: id });
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: Boolean(id && String(project || "").trim()),
    staleTime: 15 * 1000,
    retry: false,
  });
}

/**
 * List non-archived profiles for every dashboard-accessible business (`list_all`).
 * The Edge Function resolves business ids from the Finitless token (no `business_ids` in the body).
 * @param {{ enabled?: boolean }} [options]
 */
export function useLinkInBioFranchiseList(options = {}) {
  const enabled = options.enabled !== false;
  const project = useApiStore((s) => s.project);

  const query = useQuery({
    queryKey: franchiseListKey(project),
    queryFn: async () => {
      const res = await invokeLinkInBioAdmin("list_all", {});
      const rows = Array.isArray(res.data) ? res.data : [];
      return rows
        .map((r) => ({
          ...r,
          business_id:
            r?.business_id != null && String(r.business_id).trim() !== ""
              ? String(r.business_id)
              : "",
        }))
        .sort((a, b) =>
          String(a.slug || "").localeCompare(String(b.slug || "")),
        );
    },
    enabled: enabled && Boolean(String(project || "").trim()),
    staleTime: 15 * 1000,
    retry: false,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Load one profile for editing (`get` by `profile_id` + project — no `business_id` required).
 * @param {string | number | null | undefined} businessId — used for upsert default only
 * @param {string | null | undefined} profileId — UUID, or `"new"` for create (query disabled)
 */
export function useLinkInBioAdmin(businessId, profileId) {
  const id = businessId != null ? String(businessId) : "";
  const pid = profileId != null ? String(profileId) : "";
  const project = useApiStore((s) => s.project);
  const queryClient = useQueryClient();

  const isCreate = pid === "new";
  const queryEnabled =
    Boolean(pid) && !isCreate && Boolean(String(project || "").trim());

  const query = useQuery({
    queryKey: adminDetailQueryKey(pid, project),
    queryFn: async () => {
      const res = await invokeLinkInBioAdmin("get", {
        profile_id: pid,
      });
      return res.data ?? null;
    },
    enabled: queryEnabled,
    staleTime: 30 * 1000,
    retry: false,
  });

  const upsertMutation = useMutation({
    /**
     * @param {Record<string, unknown> | { business_id?: string, row: Record<string, unknown> }} variables
     * Pass `{ business_id, row }` so the request uses the id from the click/save moment (avoids stale `id` from hook scope).
     */
    mutationFn: async (variables) => {
      const wrapped =
        variables &&
        typeof variables === "object" &&
        variables.row !== undefined &&
        variables.row !== null;
      const row = wrapped ? variables.row : variables;
      const explicit =
        wrapped &&
        variables.business_id != null &&
        String(variables.business_id).trim() !== ""
          ? String(variables.business_id).trim()
          : "";
      const business_id = explicit || id;
      const res = await invokeLinkInBioAdmin("upsert", {
        business_id,
        row,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["link-in-bio-list"] });
      if (pid && !isCreate) {
        queryClient.invalidateQueries({
          queryKey: adminDetailQueryKey(pid, project),
        });
      }
    },
  });

  return {
    ...query,
    upsertMutation,
    isCreate,
  };
}

/**
 * @param {string | number | null | undefined} businessId
 */
export function useLinkInBioArchive(businessId) {
  const id = businessId != null ? String(businessId) : "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileId) => {
      await invokeLinkInBioAdmin("archive", {
        business_id: id,
        profile_id: String(profileId),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["link-in-bio-list"] });
    },
  });
}

/**
 * Permanently delete a link-in-bio row (scoped by `business_id`).
 * Pass `{ profileId, business_id }` per row (multi-store list).
 */
export function useLinkInBioDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, business_id }) => {
      const bid = String(business_id ?? "").trim();
      if (!bid) {
        throw new Error("missing_business_id");
      }
      await invokeLinkInBioAdmin("delete", {
        business_id: bid,
        profile_id: String(profileId),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["link-in-bio-list"] });
      queryClient.invalidateQueries({
        queryKey: ["link-in-bio-admin", "detail"],
      });
    },
  });
}

/**
 * Toggle `enabled` for a profile from the list (no full upsert).
 * Mutate with `{ profileId, enabled, business_id }`.
 */
export function useLinkInBioSetEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, enabled, business_id }) => {
      const bid = String(business_id ?? "").trim();
      if (!bid) {
        throw new Error("missing_business_id");
      }
      const res = await invokeLinkInBioAdmin("set_enabled", {
        business_id: bid,
        profile_id: String(profileId),
        enabled: Boolean(enabled),
      });
      return res.data ?? null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["link-in-bio-list"] });
      queryClient.invalidateQueries({
        queryKey: ["link-in-bio-admin", "detail"],
      });
    },
  });
}

/**
 * Global slug uniqueness (`check_slug` — no `business_id` in the request).
 * @param {string} slug
 * @param {boolean} enabled
 * @param {string | null | undefined} excludeProfileId — current row id when editing
 */
export function useLinkInBioSlugAvailability(slug, enabled, excludeProfileId) {
  const normalized = slug.trim().toLowerCase();
  const exclude =
    excludeProfileId != null ? String(excludeProfileId).trim() : "";
  const project = useApiStore((s) => s.project);

  return useQuery({
    queryKey: ["link-in-bio-slug", normalized, exclude, project],
    queryFn: async () => {
      const res = await invokeLinkInBioAdmin("check_slug", {
        slug: normalized,
        exclude_profile_id: exclude || undefined,
      });
      return Boolean(res.available);
    },
    enabled: Boolean(
      enabled && normalized.length >= 3 && String(project || "").trim(),
    ),
    staleTime: 10 * 1000,
    retry: false,
  });
}
